/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// =================================================================================================
// Prescription Frame + Lens Pairing System
// =================================================================================================

(function() {
  'use strict';

  // Generate a unique pair ID for frame + lens products
  function generatePairId() {
    return 'pair_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Set pair IDs before form submission for prescription products
  // Use capture phase to ensure this runs BEFORE the ProductForm handler
  document.addEventListener('submit', function(evt) {
    const form = evt.target;

    // Check if this is a dual product form (frame + lens)
    if (form.classList.contains('js-dual-product-form') || form.querySelector('[data-frame-pair-id]')) {
      const framePairIdInput = form.querySelector('[data-frame-pair-id]');
      const lensPairIdInput = form.querySelector('[data-lens-pair-id]');

      if (framePairIdInput && lensPairIdInput) {
        // Generate a unique pair ID
        const pairId = generatePairId();

        // Set the same pair ID for both frame and lens
        framePairIdInput.value = pairId;
        lensPairIdInput.value = pairId;

        console.log('Prescription pair ID generated:', pairId);
      }
    }
  }, true); // Use capture phase to run before bubble phase handlers

  // Track if we're currently removing orphaned lenses to prevent loops
  let isRemovingOrphans = false;

  // After cart updates, check for orphaned lens products and remove them
  document.addEventListener('on:cart:after-merge', function() {
    if (isRemovingOrphans) return;

    // Get the current cart state
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Find all items with _pair_id
        const pairedItems = cart.items.filter(item =>
          item.properties && item.properties._pair_id && item.properties._pair_id !== ''
        );

        if (pairedItems.length === 0) return;

        // Group items by their pair ID
        const pairGroups = {};
        pairedItems.forEach(item => {
          const pairId = item.properties._pair_id;
          if (!pairGroups[pairId]) {
            pairGroups[pairId] = [];
          }
          pairGroups[pairId].push(item);
        });

        // Find orphaned lenses (lenses without their frame)
        const orphanedLenses = [];
        Object.entries(pairGroups).forEach(([pairId, items]) => {
          // Check if this group has both frame and lens
          const hasFrame = items.some(item => !item.properties._frame_variant_id);
          const lenses = items.filter(item => item.properties._frame_variant_id);

          // If there are lenses but no frame, they're orphaned
          if (!hasFrame && lenses.length > 0) {
            console.log('Found orphaned lenses for pair:', pairId, lenses);
            orphanedLenses.push(...lenses);
          }
        });

        // Remove orphaned lenses
        if (orphanedLenses.length > 0) {
          console.log('Removing orphaned lenses:', orphanedLenses.map(l => l.title));
          isRemovingOrphans = true;

          const updates = {};
          orphanedLenses.forEach(lens => {
            updates[lens.key] = 0;
          });

          fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
          })
          .then(response => response.json())
          .then(() => {
            isRemovingOrphans = false;
            // Trigger cart refresh to update the UI
            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
            );
          })
          .catch(error => {
            console.error('Error removing orphaned lens products:', error);
            isRemovingOrphans = false;
          });
        }
      })
      .catch(error => {
        console.error('Error checking cart for orphaned items:', error);
      });
  });

})();

// =================================================================================================
// Variant-based Image Filtering by ALT Text
// =================================================================================================

(function() {
  'use strict';

  function filterMediaByVariant(gallery, variant) {
    console.log('[Image Filter] Starting filter...', { gallery, variant });

    if (!gallery || !variant) {
      console.log('[Image Filter] No gallery or variant, exiting');
      return;
    }

    // Skip filtering for prescription products (they have their own logic)
    const product = gallery.closest('.js-product');
    if (product && product.hasAttribute('data-dual-product')) {
      console.log('[Image Filter] Prescription product detected, skipping filter');
      return;
    }

    // Only filter for collage/columns layouts (not carousel)
    const galleryLayout = gallery.className.match(/media-gallery--layout-(\S+)/)?.[1];
    console.log('[Image Filter] Gallery layout:', galleryLayout);

    if (!galleryLayout || galleryLayout.includes('carousel')) {
      console.log('[Image Filter] Carousel layout, skipping filter');
      return;
    }

    // Detect which options are Frame Color and Lens Color by reading option selector labels
    const variantPicker = product?.querySelector('variant-picker');
    if (!variantPicker) {
      console.log('[Image Filter] No variant picker found');
      return;
    }

    // Get option names from the option selectors in the DOM
    const optionSelectors = variantPicker.querySelectorAll('.option-selector');
    console.log('[Image Filter] Found option selectors:', optionSelectors.length);

    let frameColorIndex = -1;
    let lensColorIndex = -1;

    optionSelectors.forEach((selector, index) => {
      // Get the option name from the data-option attribute
      const optionName = (selector.getAttribute('data-option') || '').toLowerCase().trim();
      console.log('[Image Filter] Option', index + 1, 'name:', optionName);

      if (optionName.includes('frame') && optionName.includes('color')) {
        frameColorIndex = index;
        console.log('[Image Filter] Found Frame Color at option index:', index + 1);
      } else if (optionName.includes('lens') && optionName.includes('color')) {
        lensColorIndex = index;
        console.log('[Image Filter] Found Lens Color at option index:', index + 1);
      }
    });

    // If we can't find the options, bail out
    if (frameColorIndex === -1 || lensColorIndex === -1) {
      console.log('[Image Filter] Could not find Frame Color or Lens Color options. frameColorIndex:', frameColorIndex, 'lensColorIndex:', lensColorIndex);
      return;
    }

    // Get the actual values (frameColorIndex is 0-based, so add 1 for option1, option2, etc.)
    const frameColor = (variant[`option${frameColorIndex + 1}`] || '').toLowerCase().trim();
    const lensColor = (variant[`option${lensColorIndex + 1}`] || '').toLowerCase().trim();
    console.log('[Image Filter] Selected values - Frame Color:', frameColor, 'Lens Color:', lensColor);

    // Get all media items
    const mediaLists = [
      gallery.querySelectorAll('.main-image .slider__item'),
      gallery.querySelectorAll('.product-media-collage__item'),
      gallery.querySelectorAll('.thumbnails .slider__item')
    ];

    console.log('[Image Filter] Found media lists with counts:', mediaLists.map(list => list.length));

    mediaLists.forEach((list, listIndex) => {
      // First pass: determine visibility
      list.forEach((mediaItem, itemIndex) => {
        // First, check if this item is hidden by media grouping (wrong frame color)
        // We store this in a data attribute so we can distinguish between media grouping hiding it vs our lens filter hiding it
        const hiddenByMediaGrouping = mediaItem.hidden && !mediaItem.hasAttribute('data-lens-filtered');

        if (hiddenByMediaGrouping) {
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: Hidden by media grouping (wrong frame color), skipping`);
          return;
        }

        // If we previously hid this item with lens filtering, unhide it first so we can re-evaluate
        if (mediaItem.hasAttribute('data-lens-filtered')) {
          mediaItem.hidden = false;
          mediaItem.removeAttribute('data-lens-filtered');
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: Unhiding previously lens-filtered item`);
        }

        // Get the ALT text from the image
        const img = mediaItem.querySelector('img');
        if (!img) {
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: No image found`);
          return;
        }

        const altText = (img.alt || '').toLowerCase().trim();
        console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: ALT text = "${altText}"`);

        // If no ALT text, show it (generic image)
        if (!altText) {
          mediaItem.hidden = false;
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: No ALT text, showing (generic)`);
          return;
        }

        // Parse ALT text: "frame_color|lens_color"
        const altParts = altText.split('|').map(part => part.trim());
        console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: ALT parts =`, altParts);

        let matches = false;

        if (altParts.length === 1) {
          // Just frame color
          matches = altParts[0] === frameColor;
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: Single part check: "${altParts[0]}" === "${frameColor}" = ${matches}`);
        } else if (altParts.length >= 2) {
          // frame_color|lens_color
          matches = altParts[0] === frameColor && altParts[1] === lensColor;
          console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: Two part check: "${altParts[0]}" === "${frameColor}" && "${altParts[1]}" === "${lensColor}" = ${matches}`);
        }

        mediaItem.hidden = !matches;

        // Mark items we hide with lens filtering so we can unhide them later
        if (!matches) {
          mediaItem.setAttribute('data-lens-filtered', 'true');
        }

        console.log(`[Image Filter] List ${listIndex}, Item ${itemIndex}: Final hidden state = ${mediaItem.hidden}`);
      });

      // Second pass: Fix layout by applying position-based classes to visible items only
      // This is needed because CSS :first-child doesn't account for hidden elements
      if (list.length > 0 && list[0].classList.contains('product-media-collage__item')) {
        // Only apply this fix to collage layouts
        const galleryLayout = gallery.className.match(/media-gallery--layout-(\S+)/)?.[1];

        // Remove old position classes from all items
        list.forEach(item => {
          item.classList.remove('js-first-visible', 'js-large-item');
        });

        // Find visible items and mark them
        const visibleItems = Array.from(list).filter(item => !item.hidden);

        if ((galleryLayout === 'collage-1' || galleryLayout === 'columns-2') && visibleItems.length > 0) {
          // Collage-1 and Columns-2: First visible item should be large
          visibleItems[0].classList.add('js-first-visible', 'js-large-item');
          console.log(`[Image Filter] List ${listIndex}: Marked item as first visible for ${galleryLayout} layout`);
        } else if (galleryLayout === 'collage-2') {
          // Collage-2: Every 3rd visible item (0, 3, 6...) should be large
          visibleItems.forEach((item, idx) => {
            if (idx % 3 === 0) {
              item.classList.add('js-large-item');
              console.log(`[Image Filter] List ${listIndex}: Marked item ${idx} as large for collage-2 layout`);
            }
          });
        }
      }

      // Refresh slider if needed
      if (list.length > 0) {
        const slider = list[0].closest('carousel-slider');
        if (slider?.offsetParent) {
          console.log(`[Image Filter] Refreshing slider for list ${listIndex}`);
          slider.refresh();
        }
      }
    });

    console.log('[Image Filter] Filter complete');
  }

  // Listen for variant changes
  document.addEventListener('on:variant:change', function(evt) {
    const gallery = evt.target.closest('.js-product')?.querySelector('media-gallery');
    const variant = evt.detail.variant;
    filterMediaByVariant(gallery, variant);
  });

  // Run on page load
  window.addEventListener('load', function() {
    const galleries = document.querySelectorAll('media-gallery');
    galleries.forEach(gallery => {
      const product = gallery.closest('.js-product');
      const variantPicker = product?.querySelector('variant-picker');
      if (variantPicker) {
        // Wait for variant picker to be loaded
        if (variantPicker.hasAttribute('loaded')) {
          const variant = variantPicker.variant;
          filterMediaByVariant(gallery, variant);
        } else {
          // Wait for it to load
          const observer = new MutationObserver(() => {
            if (variantPicker.hasAttribute('loaded')) {
              const variant = variantPicker.variant;
              filterMediaByVariant(gallery, variant);
              observer.disconnect();
            }
          });
          observer.observe(variantPicker, { attributes: true });
        }
      }
    });
  });
})();

/**
 * Variant Richtext Block Handler
 * Updates the variant richtext block content when the variant changes
 */
(function() {
  function updateVariantRichtext(productSection, variant) {
    const richtextBlocks = productSection.querySelectorAll('.js-variant-richtext');

    richtextBlocks.forEach(block => {
      const dataEl = block.parentNode.querySelector('.js-variant-richtext-data');
      const wrapper = block.closest('.variant-richtext-block');
      if (!dataEl) return;

      try {
        const data = JSON.parse(dataEl.textContent);
        const content = variant && data[variant.id] ? data[variant.id] : '';
        block.innerHTML = content;

        // Hide/show the block based on whether there's content
        if (wrapper) {
          wrapper.classList.toggle('hidden', !content);
        }
      } catch (e) {
        console.error('Error updating variant richtext:', e);
      }
    });
  }

  // Listen for variant changes
  document.addEventListener('on:variant:change', function(evt) {
    const productSection = evt.target.closest('.js-product');
    const variant = evt.detail.variant;
    if (productSection) {
      updateVariantRichtext(productSection, variant);
    }
  });
})();
